import { API_BASE_URL } from '../config/api';

export const getSocialLinksRaw = async (allowedPlatforms = ['facebook', 'instagram', 'youtube']) => {
    const endpoints = [
        `${API_BASE_URL}app_links`,
        `${API_BASE_URL}app-links`,
        `${API_BASE_URL}social_links`,
        `${API_BASE_URL}social-links`,
    ];

    let lastError = null;
    for (const endpoint of endpoints) {
        try {
            const res = await fetch(endpoint);
            const contentType = res.headers.get('content-type') || '';
            if (!res.ok) {
                const text = await res.text();
                console.warn('getSocialLinksRaw: non-OK response', endpoint, res.status, text);
                const e = new Error(`HTTP ${res.status}`);
                e.status = res.status;
                e.statusText = res.statusText;
                e.endpoint = endpoint;
                e.bodySnippet = text?.slice?.(0, 400) || null;
                lastError = e;
                continue;
            }
            if (!/json/i.test(contentType)) {
                const text = await res.text();
                console.warn('getSocialLinksRaw: non-JSON response for', endpoint, 'status=', res.status, 'body=', text?.slice?.(0, 400));
                // Avoid throwing so we can try other endpoints; but keep last error to report
                const e = new Error('Non-JSON response');
                e.status = res.status;
                e.statusText = res.statusText;
                e.endpoint = endpoint;
                e.bodySnippet = text?.slice?.(0, 400) || null;
                lastError = e;
                continue;
            }
            let data = null;
            // Try to parse JSON carefully — some servers set wrong content-type or return HTML error pages
            try {
                data = await res.json();
            } catch (parseErr) {
                const text = await res.text();
                // If the response body starts with '<', it's an HTML error page; log and skip.
                if (text && typeof text === 'string' && text.trim().startsWith('<')) {
                    console.warn('getSocialLinksRaw: server returned HTML (likely error page) for', endpoint);
                    lastError = new Error('Non-JSON response');
                    continue;
                }
                // Try to parse again from text (maybe wrong content-type but JSON body)
                try {
                    data = JSON.parse(text);
                } catch (err2) {
                    console.warn('getSocialLinksRaw: failed to parse JSON for', endpoint, parseErr, err2);
                    lastError = parseErr;
                    continue;
                }
            }
            console.log("SOCIAL LINKS API RESPONSE from ", endpoint, data);

            let links = [];
            if (Array.isArray(data)) links = data;
            else if (Array.isArray(data.data)) links = data.data;
            else if (Array.isArray(data.result)) links = data.result;
            else if (Array.isArray(data.links)) links = data.links;
            else if (Array.isArray(data.app_links)) links = data.app_links;

            // If it's an object, attempt to find the first array anywhere in the structure
            if (links.length === 0 && data && typeof data === 'object') {
                const seen = new Set();
                // Queue elements are { node, parentKey }
                const queue = [{ node: data, parentKey: null }];
                const candidateLinks = [];

                while (queue.length > 0) {
                    const { node, parentKey } = queue.shift();
                    if (!node || typeof node !== 'object' || seen.has(node)) continue;
                    seen.add(node);

                    if (Array.isArray(node)) {
                        links = node;
                        break;
                    }

                    // If this node has a URL-like field, map it
                    const urlKey = ['url', 'link', 'href', 'path', 'value'].find(k => typeof node[k] === 'string' && node[k].trim() !== '');
                    if (urlKey) {
                        const urlVal = node[urlKey];
                        const name = node.name || node.title || parentKey || node.key || null;
                        candidateLinks.push({ name: name || `link_${candidateLinks.length}`, url: urlVal, meta: node });
                        // We continue to scan to find more
                    }

                    // Map object-of-strings directly as links
                    const entries = Object.entries(node);
                    if (entries.length > 0 && entries.every(([, v]) => typeof v === 'string')) {
                        for (const [k, v] of entries) {
                            // Recognize both name/url entries and plain key -> url
                            if (/^(https?:)?\/\//i.test(v) || v.startsWith('/')) {
                                candidateLinks.push({ name: k, url: v });
                            }
                        }
                    }

                    for (const [k, v] of entries) {
                        if (v && typeof v === 'object') queue.push({ node: v, parentKey: k });
                    }
                }

                if (links.length === 0 && candidateLinks.length > 0) {
                    links = candidateLinks;
                }
            }

            // Normalize links to objects with { name, url }
            const normalized = links.map((item, idx) => {
                if (typeof item === 'string') return { name: `link_${idx}`, url: item };
                if (!item || typeof item !== 'object') return null;
                const name = item.name || item.title || item.key || item.platform || item.name || `link_${idx}`;
                const url = item.url || item.link || item.href || item.path || item.value || null;
                return { ...item, name, url };
            }).filter(Boolean);

            // Apply strict platform detection
            const allowed = Array.isArray(allowedPlatforms) && allowedPlatforms.length > 0
                ? allowedPlatforms.map(p => p.toLowerCase())
                : null;

            const normalizeName = (s) => (s || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
            const detectPlatformFromName = (name) => {
                const norm = normalizeName(name);
                if (norm.includes('facebook') || norm === 'fb') return 'facebook';
                if (norm.includes('instagram') || norm === 'insta' || norm === 'ig') return 'instagram';
                if (norm.includes('youtube') || norm === 'yt' || norm === 'youtub') return 'youtube';
                return null;
            };
            const detectPlatformFromUrl = (url) => {
                if (!url || typeof url !== 'string') return null;
                const u = url.toLowerCase();
                if (u.includes('facebook.com') || u.includes('fb.me')) return 'facebook';
                if (u.includes('instagram.com') || u.includes('instagrm') || u.includes('ig.me')) return 'instagram';
                if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
                return null;
            };

            const withPlatform = normalized.map(item => {
                const platformFromName = detectPlatformFromName(item.name || item.title || item.key || item.platform);
                const platformFromUrl = detectPlatformFromUrl(item.url);
                const platform = platformFromName || platformFromUrl || null;
                return { ...item, platform };
            });

            console.debug('getSocialLinksRaw: normalized', normalized);
            console.debug('getSocialLinksRaw: withPlatform', withPlatform);

            const filtered = allowed
                ? withPlatform.filter(it => it?.url && allowed.some(p => it.platform === p))
                : withPlatform;

            console.debug('getSocialLinksRaw: filtered', filtered);

            return { links: withPlatform, filteredLinks: filtered, raw: data, endpoint, status: res.status, statusText: res.statusText };
        } catch (err) {
            console.warn('getSocialLinksRaw failed for', endpoint, err?.message || err);
            lastError = err;
            continue;
        }
    }

    if (lastError) throw lastError;
    return { links: [], raw: null, endpoint: null };
};

export const getSocialLinks = async () => {
    const res = await getSocialLinksRaw();
    return res?.filteredLinks || res?.links || [];
};
export default getSocialLinks;
